import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

// minimum pixel offset to avoid false positives due to subpixel rounding
const SCROLL_EDGE_THRESHOLD = 2;

export const useScrollDetection = (ref: RefObject<HTMLDivElement | null>) => {
  const [hasLeftScroll, setHasLeftScroll] = useState(false);
  const [hasRightScroll, setHasRightScroll] = useState(false);

  const checkScroll = useCallback(() => {
    const node = ref.current;
    if (!node) return;

    const { scrollLeft, scrollWidth, clientWidth } = node;
    setHasLeftScroll(scrollLeft > SCROLL_EDGE_THRESHOLD);
    setHasRightScroll(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD);
  }, [ref]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new MutationObserver(checkScroll);
    observer.observe(node, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(node);
    checkScroll();
    return () => {
      node.removeEventListener('scroll', checkScroll);
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [checkScroll, ref]);

  return {
    hasLeftScroll,
    hasRightScroll,
  };
};
